import { ContactPill } from './ContactPill'

/** One person on the Responsibility card. `onOpen` adds a "View" button → that person's view (officer
 *  accountability / HOD workload); `openTitle` is the button's tooltip. */
export interface Person {
  roleLabel: string // e.g. "Field Officer" or "Head of Dept · BC Welfare"
  name: string
  sub?: string | null // designation
  phone?: string | null
  email?: string | null
  avatar: 'fo' | 'hod'
  onOpen?: () => void
  openTitle?: string
}

function initials(n: string): string {
  const x = n.replace(/[^A-Za-z ]/g, '').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
  return x || '?'
}

const AVATAR: Record<'fo' | 'hod', string> = {
  fo: 'bg-[linear-gradient(140deg,#2f6fe0,#1d4ed8)]',
  hod: 'bg-[linear-gradient(140deg,#1b2c52,#0f1f3d)]',
}

/** One person as a bordered card: a left block (avatar + role/name/designation + contact pills) and —
 *  when the person is openable — a "View" button on the RIGHT (opens their accountability / workload). */
function PersonRow({ p }: { p: Person }) {
  return (
    <div className="flex items-center gap-3 rounded-[12px] border border-gis-line bg-white p-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-[10px]">
          <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-[9px] text-[11.5px] font-bold text-white ${AVATAR[p.avatar]}`}>
            {initials(p.name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[9.5px] font-bold tracking-[.05em] text-gis-subheading uppercase">{p.roleLabel}</div>
            <div className="mt-0.5 truncate text-[12px] font-semibold text-gis-ink">{p.name}</div>
            {p.sub && <div className="truncate text-[11px] font-medium text-gis-soft">{p.sub}</div>}
          </div>
        </div>
        {(p.phone || p.email) && (
          <div className="mt-2 flex flex-wrap gap-1.5 pl-[42px]">
            <ContactPill phone={p.phone ?? null} email={p.email ?? null} size="sm" />
          </div>
        )}
      </div>
      {p.onOpen && (
        <button
          type="button"
          onClick={p.onOpen}
          title={p.openTitle ?? 'Open'}
          className="inline-flex flex-none items-center rounded-full bg-gis-navy px-4 py-[7px] text-[12px] font-semibold text-white transition hover:brightness-110 active:brightness-95 focus-visible:ring-2 focus-visible:ring-blue-500/60"
        >
          View
        </button>
      )}
    </div>
  )
}

/** Responsibility roster — one bordered card per person. Renders only people with a name. */
export function PeopleCard({ people }: { people: Person[] }) {
  const visible = people.filter((p) => p.name.trim().length > 0)
  if (visible.length === 0) {
    return null
  }
  return (
    <div className="flex flex-col gap-2.5">
      {visible.map((p) => (
        <PersonRow key={p.roleLabel} p={p} />
      ))}
    </div>
  )
}
